import React from 'react';
import Button from '../common/Button';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';

const { FiMail, FiFileText, FiShield, FiArrowRight } = FiIcons;
const { FaApple, FaGooglePlay } = FaIcons;

const APP_STORE_URL = "https://apps.apple.com/us/app/nisaa/id6772802488";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=uk.co.sussexendocrineclinic.nisaa";

const ContactCard = ({ icon: Icon, title, description, action, href, variant = "secondary" }) => (
  <div className="bg-white p-8 rounded-[32px] border border-line/60 shadow-soft hover:shadow-card transition-all duration-300 flex flex-col items-start h-full">
    <div className="w-12 h-12 rounded-2xl bg-rose/10 text-rose flex items-center justify-center mb-6">
      <SafeIcon icon={Icon} className="w-6 h-6" />
    </div>
    <h3 className="text-xl font-bold text-aubergine mb-3">{title}</h3>
    <p className="text-muted text-sm leading-relaxed mb-8 flex-grow">{description}</p>
    <Button href={href} variant={variant} className="w-full">
      {action}
    </Button>
  </div>
);

const ContactSection = () => {
  return (
    <section id="contact" className="py-24 lg:py-32 bg-paper relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-white to-transparent" />
      
      <div className="w-full max-w-[1120px] mx-auto px-4 relative z-10">
        <div className="text-center max-w-[700px] mx-auto mb-16">
          <h2 className="text-[clamp(2.5rem, 4vw, 3.5rem)] mb-6">We're here to help</h2>
          <p className="text-lg text-muted">
            Whether you have questions about the app, privacy concerns, or just want to say hello, we'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <ContactCard 
            icon={FiMail} 
            title="General Inquiries" 
            description="Have a question about the app or need help with your account? Our support team is ready to assist." 
            action="Email Support" 
            href="mailto:contact@sussexendocrineclinic.co.uk" 
            variant="primary"
          />
          <ContactCard 
            icon={FiShield} 
            title="Privacy & Data" 
            description="Request data export, account deletion, or ask about our privacy standards and security measures." 
            action="Privacy Request" 
            href="mailto:contact@sussexendocrineclinic.co.uk?subject=Privacy%20Inquiry"
          />
          <ContactCard 
            icon={FiFileText} 
            title="Medical & Religious" 
            description="Read our comprehensive disclaimers regarding medical information and religious guidance." 
            action="Read Disclaimers" 
            href="/#/disclaimer"
          />
        </div>

        <div className="mt-16 p-8 lg:p-12 rounded-[40px] bg-aubergine text-white flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose/20 rounded-full blur-[80px] -mr-32 -mt-32" />
          <div className="relative z-10">
            <h3 className="text-2xl lg:text-3xl text-white mb-2">Ready to start?</h3>
            <p className="text-white/70">Download Nisaa now and take control of your health journey.</p>
          </div>
          
          <div className="flex flex-wrap gap-4 relative z-10">
            <a 
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-aubergine hover:bg-rose hover:text-white px-8 py-3 rounded-2xl font-bold transition-all flex items-center gap-2"
            >
              <SafeIcon icon={FaApple} className="w-5 h-5" />
              App Store
            </a>
            <a 
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 text-white hover:bg-white/20 px-8 py-3 rounded-2xl font-bold transition-all border border-white/20 flex items-center gap-2"
            >
              <SafeIcon icon={FaGooglePlay} className="w-4 h-4" />
              Google Play
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;